import { Module } from 'vuex';
import { pick } from 'lodash';

import api from '../../api/modules/media-libraries';
import * as types from '../mutation-types';
import { mapWithLimit } from '@/core/utils/concurrency';

interface State {
  media: PaginatedResponse<TeacherMediaResponse> | null;
  categories: TeacherCategoryResponse[] | null;
  instructors: InstructorResponse[] | null;
}

const initialState: State = {
  media: null,
  categories: null,
  instructors: [],
};

const emptyMedia = {
  count: null,
  next: null,
  previous: null,
  results: [],
};

const mediaLibraryModule: Module<State, any> = {
  namespaced: true,
  state: initialState,
  getters: {
    media({ media }) {
      return media || emptyMedia;
    },
    categoriesTags({ categories }): any {
      return categories?.reduce(
        (options: any, category: any) => [
          ...options,
          ...category.option_list.map(option => ({
            name: `${category.name} - ${option.name}`,
            id: option.id,
          })),
        ],
        []
      );
    },
    instructors: state => state.instructors,
    canAddInstructor(state, getters, rootState, rootGetters) {
      if (state.instructors) {
        const maxInstructors = rootGetters['user/maxInstructors'];
        return state.instructors.length < maxInstructors;
      } else return false;
    },
  },
  actions: {
    async getMedia({ commit }, params): Promise<any> {
      const { page, fileType, instructor, options, q } = pick(params, [
        'page',
        'fileType',
        'instructor',
        'options',
        'q',
      ]);
      const data = await api.getTeacherMedia(
        page,
        fileType,
        instructor,
        options,
        q
      );
      commit(types.SET_MEDIA, data);
      return data;
    },
    async getVideoDownloadLink(_, userFileId): Promise<string> {
      return await api.getVideoDownloadLink(userFileId);
    },
    async duplicate(_, mediaId): Promise<string> {
      return await api.duplicate(mediaId);
    },
    async canUploadVideo(_): Promise<any> {
      await api.canUploadVideo();
    },
    async getMediaViews({ commit }, params): Promise<any> {
      const { mediaId, page, q } = pick(params, ['mediaId', 'page', 'q']);
      const data = await api.getMediaViews(mediaId, page, q);
      return data;
    },
    async getMediaViewsCSV(_, mediaId) {
      const data = await api.getMediaViewsCSV(mediaId);
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `media-views-${mediaId}.csv`);
    },
    async getMediaLikes({ commit }, params): Promise<any> {
      const { mediaId, page, q } = pick(params, ['mediaId', 'page', 'q']);
      const data = await api.getMediaLikes(mediaId, page, q);
      return data;
    },
    async getMediaLikesCSV(_, mediaId) {
      const data = await api.getMediaLikesCSV(mediaId);
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `media-views-${mediaId}.csv`);
    },
    async createMedia(
      _,
      data: CreateMediaRequest
    ): Promise<TeacherMediaResponse> {
      const result = await api.createMedia(data);
      return result;
    },
    async updateMedia(
      _,
      data: UpdateMediaRequest
    ): Promise<TeacherMediaResponse> {
      const result = await api.updateMedia(data);
      return result;
    },
    async updatePlayback(
      _,
      { id, playback_start, playback_end }
    ): Promise<void> {
      await api.updatePlayback({ id, playback_start, playback_end });
    },
    async trimVideo(_, { id, trimStart, trimEnd }): Promise<void> {
      await api.trimVideo(id, trimStart, trimEnd);
    },
    async checkTrimProgress(_, id): Promise<void> {
      return await api.checkTrimProgress(id);
    },
    async updateMediaFile(_, { id, data }): Promise<void> {
      await api.updateMediaFile(id, data);
    },
    async toggleFileDeletable(_, { id }): Promise<void> {
      await api.toggleFileDeletable(id);
    },
    async getMediaById(_, id): Promise<TeacherMediaResponse> {
      const result = await api.getMedia(id);
      return result;
    },
    async deleteMedia(_, id): Promise<void> {
      await api.deleteMedia(id);
    },
    async getCategories({ commit }): Promise<void> {
      const data = await api.getTeacherCategories();
      commit(types.SET_CATEGORIES, data);
    },
    async createCategory(
      _,
      data: CreateCategoryRequest
    ): Promise<TeacherCategoryResponse> {
      const result = await api.createCategory(data);
      return result;
    },
    async updateCategory(
      _,
      data: UpdateCategoryRequest
    ): Promise<TeacherCategoryResponse> {
      const result = await api.updateCategory(data);
      return result;
    },
    async deleteCategory({ state, commit }, id: number): Promise<void> {
      const result = await api.deleteCategory(id);

      commit(
        types.SET_CATEGORIES,
        state.categories?.filter(category => category.category_id !== id)
      );

      return result;
    },
    async reorderCategories(
      { commit, dispatch },
      categories: Array<TeacherCategoryResponse>
    ) {
      commit(types.SET_CATEGORIES, categories);
      await mapWithLimit(categories, category =>
        dispatch('updateCategory', category)
      );
    },
    async getInstructors({ commit }): Promise<void> {
      const data = await api.getTeacherInstructors();
      commit(types.SET_INSTRUCTORS, data);
    },
    async createInstructor(
      _,
      data: CreateInstructorRequest
    ): Promise<InstructorResponse> {
      const result = await api.createInstructor(data);
      return result;
    },
    async deleteInstructorPhoto({ state, commit }, id: number): Promise<void> {
      const result = await api.deleteInstructorPhoto(id);

      commit(types.SET_INSTRUCTORS, state.instructors);

      return result;
    },
    async updateInstructor(
      _,
      data: UpdateInstructorRequest
    ): Promise<InstructorResponse> {
      const result = await api.updateInstructor(data);
      return result;
    },
    async deleteInstructor({ state, commit }, id: number): Promise<void> {
      const result = await api.deleteInstructor(id);

      commit(
        types.SET_INSTRUCTORS,
        state.instructors?.filter(instructor => instructor.id !== id)
      );

      return result;
    },
    async reorderInstructors(
      { commit, dispatch },
      instructors: Array<InstructorResponse>
    ) {
      await mapWithLimit(instructors, instructor =>
        dispatch('updateInstructor', instructor)
      );
    },
    async searchUnsplashImages(_, { query, page, pageSize }): Promise<any> {
      const result = await api.searchUnsplashImages(query, page, pageSize);
      return result;
    },
    async unsplashMarkDownload(_, { downloadUrl }): Promise<any> {
      const result = await api.unsplashMarkDownload(downloadUrl);
      return result;
    },
  },
  mutations: {
    [types.SET_MEDIA](
      state: State,
      data: PaginatedResponse<TeacherMediaResponse>
    ) {
      state.media = data;
    },
    [types.SET_CATEGORIES](state: State, data: TeacherCategoryResponse[]) {
      state.categories = data;
    },
    [types.SET_INSTRUCTORS](state: State, data: InstructorResponse[]) {
      state.instructors = data;
    },
  },
};

export default mediaLibraryModule;
